import { useOrderHeader } from "hooks/order";
import { useCallback } from "react";
import Input from "components/ui/Input";

export default function ItemSizesTable({ row, rowIndex }) {
  const { columnOrderItems, setColumnOrderItems, sizeToColumnMap } = useOrderHeader();
  
  // Use itemSizes from row data instead of making API call
  const itemSizes = row.itemSizes || [];

  const updateSizeQuantity = useCallback((rowIndex, sizeInfo, quantity) => {
    setColumnOrderItems(columnOrderItems.map((row, index) => {
      if (index === rowIndex) {
        const newQuantities = [...row.quantities];
        const existingIndex = newQuantities.findIndex(q => q.agitemdtlId === sizeInfo.agitemdtlId);
        
        // Convert quantity to float and check if it's valid
        const floatQuantity = parseFloat(quantity);
        const isValidQuantity = !isNaN(floatQuantity) && floatQuantity > 0;
        
        // Generate column ID from size information and get the mapped column index
        const columnId = `${sizeInfo.sz}_${sizeInfo.szuom}`;
        const columnIndex = sizeToColumnMap.get(columnId);
        
        if (existingIndex >= 0) {
          if (isValidQuantity) {
            newQuantities[existingIndex] = {
              ...newQuantities[existingIndex],
              qty: floatQuantity.toString()
            };
          } else {
            newQuantities.splice(existingIndex, 1); // Remove if empty, null, or 0
          }
        } else if (isValidQuantity) {
          // Add new quantity entry with column index instead of columnId
          newQuantities.push({
            sz: sizeInfo.sz,
            szuom: sizeInfo.szuom,
            itmsztyp: sizeInfo.itmsztyp,
            packratio: sizeInfo.packratio,
            agitemdtlId: sizeInfo.agitemdtlId,
            qty: floatQuantity.toString(),
            col: columnIndex // Store column index from mapping
          });
        }
        
        return {
          ...row,
          quantities: newQuantities
        };
      }
      return row;
    }));
  }, [columnOrderItems, setColumnOrderItems, sizeToColumnMap]);

  // Don't render if no item or no sizes available
  if (!row.item || itemSizes.length === 0) {
    return null;
  }

  return (
    <div className="mt-2 bg-white border border-gray-200 rounded">
      <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
        <h4 className="text-sm font-medium text-gray-900">Item Sizes</h4>
      </div>
      <div className="max-h-48 overflow-y-auto">
        {itemSizes.map((size, index) => (
          <div key={size.agitemdtlId} className="flex items-center justify-between p-2 border-b border-gray-100 last:border-b-0">
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">
                {size.sz}{size.szuom !== "Roman" ? ` ${size.szuom}` : ''}
              </div>
              <div className="text-xs text-gray-500">
                Pack Ratio: {size.packratio}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Input
                type="number"
                showLabel={false}
                value={row.quantities.find(q => q.agitemdtlId === size.agitemdtlId)?.qty || ''}
                onChange={(e) => updateSizeQuantity(rowIndex, size, e.target.value)}
                className="w-20 [&_input]:px-2 [&_input]:py-1 [&_input]:text-xs"
                placeholder="Qty"
                step="0.01"
              />
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                title="More options"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 